import { CoreState, Settings } from '@/store/core/state';
import { CoreGetters } from '@/store/core/getters';
import { CoreMutations } from '@/store/core/mutations';
import { Actions } from 'vuex-smart-module';
import { Routes } from '@/router/routes';
import { NavigationFailure, RouteMeta, Router } from 'vue-router';
import type { AppInitData } from '@/types/core';
import { ValidatedConfig } from '@/entities/config';
import { AmplitudeService, ServiceContainer, StorageService, WebsiteService } from '@/services';
import { AxiosError } from 'axios';
import { captureException, setTag } from '@sentry/browser';
import { AmplitudeEvent, NavigationPayload } from '@/types/tradepending';
import { APP_CONFIG } from '@/common';

interface CoreActionsDependencies {
	router: Router;
	services: ServiceContainer;
}

export class CoreActions extends Actions<
	CoreState,
	CoreGetters,
	CoreMutations,
	CoreActions
	> {
	self = this;

	router!: Router

	storageService!: StorageService

	websiteService!: WebsiteService

	amplitudeService!: AmplitudeService

	$setDependencies(dependencies: CoreActionsDependencies): void {
		this.self.storageService = dependencies.services.storage;
		this.self.websiteService = dependencies.services.website;
		this.self.amplitudeService = dependencies.services.amplitude;
		this.self.router = dependencies.router;
	}

	toggleModal(payload?: void): void {
		this.mutations.toggleModal();
		localStorage.removeItem('DealerDomain');
	}

	async runApp(data: AppInitData): Promise<void> {
		if (APP_CONFIG.MAINTENANCE_MODE !== 'false') {
			await this.actions.navigateTo(Routes.ERROR.path);
		} else {
			this.mutations.setInitData(data);
			if (this.state.isError) { return; }
			await this.actions.navigateTo(Routes.CUSTOMIZE_DEAL.path);
		}
		this.actions.openPopup();
	}

	openPopup(payload?: void) {
		this.mutations.openPopup();
	}

	closePopup(payload?: void) {
		this.mutations.closePopup();
	}

	randomColor(payload?: void): void {
		this.mutations.randomColor();
	}

	async showError(payload?: void): Promise<void | NavigationFailure | undefined> {
		// TODO: collect error in Sentry, pass error object as payload
		return this.router.push(Routes.ERROR.path);
	}

	loadConfig(config: ValidatedConfig) {
		this.mutations.loadConfig(config);
	}

	async navigateTo(path: string): Promise<void | NavigationFailure | undefined> {
		return this.router.push(path);
	}

	loadSettings(payload?: void): void {
		if (!this.getters.isSettingsLoaded) {
			this.mutations.saveSettings(this.storageService.getSettings() as Settings || {});
			this.mutations.setSettingsLoaded();
		}
	}

	saveSettings(settings: Partial<Settings>) {
		this.mutations.saveSettings(settings);
		this.storageService.saveSettings(settings);
	}

	async loadWebsiteSettings(payload?: void): Promise<void> {
		const response = await this.websiteService.getSettings()
			.catch(error => this.actions.processError(error));

		if (response) {
			this.mutations.saveWebsiteSettings(response);
		}
	}

	async processError(payload?: AxiosError): Promise<void> {
		this.mutations.setError();
		console.error(payload);
		setTag('info', 'coreProcessError');
		captureException(payload);

		return Promise.resolve();
	}

	async loadWebsite(): Promise<void> {
		const response = await this.websiteService.getWebsiteInfo();

		if (response) {
			this.mutations.updateWebsite(response);
		}

		return Promise.resolve();
	}

	async loadDealer(dealerId: number): Promise<void> {
		const response = await this.websiteService.getDealerInfo(dealerId);

		if (response) {
			this.mutations.updateDealer(response);
		}

		return Promise.resolve();
	}

	sendEvent(payload: AmplitudeEvent) {
		const dealerDomain = this.getters.website?.domain;

		this.amplitudeService.sendEvent({ ...payload, DealerDomain: dealerDomain });
	}

	sendNavigationEvent(data: NavigationPayload) {
		const dealerDomain = this.getters.website?.domain;

		this.amplitudeService.sendNavigationEvent({ ...data, DealerDomain: dealerDomain });
	}

	sendSaveEvent(routeDetails: RouteMeta) {
		const dealerDomain = this.getters.website?.domain;

		this.amplitudeService.sendSaveEvent({ ...routeDetails, DealerDomain: dealerDomain });
	}

	async checkButtonVisibility(vin: string) {
		return this.websiteService.checkButtonVisibility(vin);
	}
}
